import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Edit2, ChevronRight, MapPin, Building2 } from "lucide-react";
import { ratingOptions } from "@/pages/clients/clientsUtils";
import PillSelect from "@/components/common/PillSelect";
import { SortableHeader, HeaderFilter } from "@/components/common/SortHeader";
import { money } from "@/lib/format";

// Top-level customer list table for /clients. Facility / Location / Rating pills in the toolbar group the rows. Column names sort (click again flips); the Facility type /
// Location / Rating filters live behind the small icon beside the column name (UI sweep rule); a quick-edit
// pencil per row, and an inline rating dropdown.
//
// Extracted from Clients.jsx (Feb 2026, iter179). State stays with
// the parent — this component only receives the resolved data and
// imperative handlers.
// Module-level so the header (and the filter popovers inside it) does not remount on every parent render.
const Th = ({ field, label, filter, testId, className = "", sortField, sortDirection, handleSort }) => (
  <TableHead className={`th-std ${className}`}>
    {field
      ? <SortableHeader label={label} sorted={sortField === field} direction={sortDirection} onSort={() => handleSort(field)} filter={filter} testId={testId} />
      : <span className="inline-flex items-center gap-1"><span className="font-semibold">{label}</span>{filter}</span>}
  </TableHead>
);

const GROUP_KEY = {
  facility: (c) => c.facility_type || "",
  location: (c) => [c.city, c.state].filter(Boolean).join(", "),
  rating: (c) => c.rating || "",
};
const GROUP_EMPTY = { facility: "No facility type", location: "No location", rating: "Unrated" };

// Flat list with group header markers; pinned rows (Parts Counter / Sandbox) stay on top, blanks group last.
function groupRows(clients, groupBy) {
  const keyOf = GROUP_KEY[groupBy];
  if (!keyOf) return clients;
  const pinned = clients.filter((c) => c.customer_type === "parts_counter" || c.is_permanent || c.is_sandbox);
  const groups = new Map();
  clients.filter((c) => !pinned.includes(c)).forEach((c) => {
    const k = keyOf(c);
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(c);
  });
  const keys = [...groups.keys()].sort((a, b) => (!a) - (!b) || a.localeCompare(b));
  return [...pinned, ...keys.flatMap((k) => [
    { group: k || "none", label: k ? (groupBy === "rating" ? `Rating ${k}` : k) : GROUP_EMPTY[groupBy], count: groups.get(k).length },
    ...groups.get(k),
  ])];
}

export function CustomerListTable({
  loading,
  sortedClients,
  groupBy = "",
  getPrimaryContact,
  handleSort, sortField, sortDirection,
  facilityTypeFilter, setFacilityTypeFilter, facilityTypes,
  locationFilter, setLocationFilter, uniqueLocations,
  ratingFilter, setRatingFilter,
  handleRatingChange,
  setEditDialogClient,
  loadClientDetails,
}) {
  const hdr = { sortField, sortDirection, handleSort };
  return (
    <Table data-testid="customer-list-table">
      <TableHeader className="thead-sticky">
        <TableRow className="bg-muted/40">
          <Th {...hdr} field="name" label="Customer" testId="sort-name" />
          <Th {...hdr} label="Contact" className="w-[190px]" />
          <Th {...hdr} field="facility_type" label="Facility" testId="sort-facility-type" className="w-[120px]" filter={
            <HeaderFilter label="Facility Type" testId="filter-facility-type" value={facilityTypeFilter || ""} onChange={(v) => setFacilityTypeFilter(v || "")}
              options={facilityTypes.filter((ft) => (ft.name || "").trim()).map((ft) => ({ value: ft.name, label: ft.name }))} />
          } />
          <Th {...hdr} field="location" label="Location" testId="sort-location" className="w-[130px]" filter={
            <HeaderFilter label="Location" testId="filter-location" value={locationFilter || ""} onChange={(v) => setLocationFilter(v || "")}
              options={uniqueLocations.map((loc) => ({ value: loc, label: loc }))} />
          } />
          <Th {...hdr} label="Equipment" className="w-[170px]" />
          <Th {...hdr} field="bid_count" label="Bid" testId="sort-total-bid" className="num w-[64px]" />
          <Th {...hdr} field="won_count" label="Won" testId="sort-total-jobs" className="num w-[64px]" />
          <Th {...hdr} field="hit_rate" label="Rate" testId="sort-hit-rate" className="num w-[64px]" />
          <Th {...hdr} field="rating" label="Rating" testId="sort-rating" className="w-[84px]" filter={
            <HeaderFilter label="Rating" testId="filter-rating" value={ratingFilter || ""} onChange={(v) => setRatingFilter(v || "")}
              options={ratingOptions.map((opt) => ({ value: opt.value, label: opt.label }))} />
          } />
        </TableRow>
      </TableHeader>
      <TableBody>
        {loading ? (
          <TableRow>
            <TableCell colSpan={8} className="text-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900 mx-auto"></div>
            </TableCell>
          </TableRow>
        ) : sortedClients.length === 0 ? (
          <TableRow>
            <TableCell colSpan={8} className="text-center py-8">
              <div className="empty-state">
                <Building2 className="empty-state-icon" />
                <p className="empty-state-title">No customers found</p>
                <p className="empty-state-text">Add your first customer to get started</p>
              </div>
            </TableCell>
          </TableRow>
        ) : (
          groupRows(sortedClients, groupBy).map((item) => {
            if (item.group) {
              return (
                <TableRow key={`grp-${item.group}`} className="bg-muted/30 hover:bg-muted/30" data-testid={`clients-group-header-${item.group}`}>
                  <TableCell colSpan={8} className="py-1.5 text-xs font-semibold text-slate-600">
                    {item.label} <span className="ml-1 font-normal text-slate-400 num">{item.count}</span>
                  </TableCell>
                </TableRow>
              );
            }
            const client = item;
            const primaryContact = getPrimaryContact(client.contacts);
            const isPartsCounter = client.customer_type === "parts_counter";
            return (
              <TableRow
                key={client.id}
                className={`table-row-hover cursor-pointer ${isPartsCounter ? "row-pinned" : ""}`}
                data-testid={`client-row-${client.id}`}
                onClick={() => loadClientDetails(client)}
              >
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    <button type="button" className="truncate text-left text-xs font-medium text-brand hover:underline" onClick={(e) => { e.stopPropagation(); loadClientDetails(client); }} data-testid={`client-name-${client.id}`}>{client.name}</button>
                    {client.customer_number && (
                      <span className="text-xs font-mono text-slate-400">#{client.customer_number}</span>
                    )}
                    {isPartsCounter && (
                      <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium bg-slate-100 text-slate-700 border-slate-200" data-testid={`parts-counter-badge-${client.id}`}>Parts counter</span>
                    )}
                    {/* Quick Edit pencil — opens a focused modal for
                        core identity fields. The row's main onClick
                        still drops you into the full side-sheet for
                        deeper edits. */}
                    <button
                      type="button"
                      className="opacity-40 hover:opacity-100 hover:text-brand transition-opacity ml-auto sm:ml-0"
                      onClick={(e) => { e.stopPropagation(); setEditDialogClient(client); }}
                      title="Quick edit name, number and type"
                      data-testid={`btn-quick-edit-${client.id}`}
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                </TableCell>
                <TableCell>
                  {primaryContact ? (
                    <div>
                      <div className="text-xs truncate max-w-[160px]">{primaryContact.name}</div>
                      <div className="text-xs text-slate-500 truncate max-w-[160px]">
                        {primaryContact.email || primaryContact.phone || "-"}
                      </div>
                    </div>
                  ) : "-"}
                </TableCell>
                <TableCell>
                  {client.facility_type ? (
                    <span className="text-xs text-slate-600">{client.facility_type}</span>
                  ) : "-"}
                </TableCell>
                <TableCell>
                  {client.city || client.state ? (
                    <div className="flex items-center gap-1 text-xs text-slate-600">
                      <MapPin className="w-3 h-3" />
                      {[client.city, client.state].filter(Boolean).join(", ")}
                    </div>
                  ) : "-"}
                </TableCell>
                <TableCell>
                  {(client.equipment_scopes || []).length > 0 ? (
                    <div className="relative group max-w-[130px]" data-testid={`equipment-scopes-${client.id}`}>
                      <span className="text-xs text-slate-600 truncate block">
                        {client.equipment_scopes.join(", ")}
                      </span>
                      <div className="absolute z-50 left-0 top-full mt-1 hidden group-hover:block bg-slate-800 text-white text-xs rounded-md px-3 py-2 shadow-lg whitespace-nowrap max-w-xs">
                        {client.equipment_scopes.map((s) => (
                          <div key={`eqsc-${s}`}>{s}</div>
                        ))}
                      </div>
                    </div>
                  ) : <span className="text-slate-400">-</span>}
                </TableCell>
                <TableCell className="num text-xs" title={`${money(client.total_bid || 0)} total bid (proposals + work orders)`} data-testid={`bid-count-${client.id}`}>{client.bid_count || 0}</TableCell>
                <TableCell className="num text-xs" title={`${client.total_jobs || 0} work order${client.total_jobs === 1 ? "" : "s"}`} data-testid={`won-count-${client.id}`}>{client.won_count || 0}</TableCell>
                <TableCell className="num text-xs" data-testid={`hit-rate-${client.id}`}>
                  {client.hit_rate == null ? <span className="text-slate-300">—</span> : (
                    <span className={client.hit_rate > 70 ? "text-emerald-600" : client.hit_rate > 30 ? "text-amber-500" : "text-red-600"}
                      title={`${client.won_count} won of ${client.bid_count} decided bids (open bids excluded)`}>{Math.round(client.hit_rate)}%</span>
                  )}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <PillSelect
                    className="w-[64px] justify-between"
                    value={client.rating || ""}
                    onChange={(value) => handleRatingChange(client.id, value === "none" ? "" : value)}
                    title="Change rating"
                    options={[{ value: "none", label: "—", pill: "bg-slate-100 text-slate-500 border-slate-200" }, ...ratingOptions.map((opt) => ({ value: opt.value, label: opt.label, pill: opt.color }))]}
                    testId={`rating-${client.id}`}
                    optionTestId={(v) => `rating-opt-${v}-${client.id}`}
                  />
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}
